"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getStateMachineFunctionsTemplate = getStateMachineFunctionsTemplate;
exports.getStateMachineInitTemplate = getStateMachineInitTemplate;
exports.getStackEncodingInitTemplate = getStackEncodingInitTemplate;
exports.getStatefulOpcodeResolutionTemplate = getStatefulOpcodeResolutionTemplate;
exports.getStateEvolutionTemplate = getStateEvolutionTemplate;
exports.getStackEncodingPushTemplate = getStackEncodingPushTemplate;
exports.getStackEncodingPopTemplate = getStackEncodingPopTemplate;
const VMConstants_1 = require("../../../constants/VMConstants");
function getStateMachineFunctionsTemplate() {
    return `
            // ===== State Machine Functions =====
            // State-dependent opcode resolution and stack encoding

            // Number of VM states
            let _SM_NUM_STATES = 8;

            // Mixing constants (must match compile-time exactly)
            let _SM_MULT1 = 0x9E3779B9;
            let _SM_MULT2 = 0x85EBCA6B;
            let _SM_MULT3 = 0xC2B2AE35;

            // Opcode count (covers all VMOpCode values including macro-ops 250-261)
            let _SM_OPCODE_COUNT = ${VMConstants_1.VM_OPCODE_COUNT};

            // Mix bits of a 32-bit integer
            function _smMixBits(h) {
                h = h >>> 0;
                h ^= h >>> 16;
                h = Math.imul(h, _SM_MULT2) >>> 0;
                h ^= h >>> 13;
                h = Math.imul(h, _SM_MULT3) >>> 0;
                h ^= h >>> 16;
                return h >>> 0;
            }

            // Compute VM state based on position (pc)
            // IMPORTANT: State is computed purely from seed and pc, NOT from execution history.
            // This is critical because control flow can jump around, and we need
            // the same state at a given pc regardless of how we got there.
            function _smComputeState(seed, pc) {
                let combined = seed ^ ((pc * _SM_MULT1) >>> 0);
                return _smMixBits(combined) % _SM_NUM_STATES;
            }

            // Evolve stack encoding key
            function _smEvolveStackKey(key, state, opcode, pc) {
                let combined = key ^ ((state * _SM_MULT1) >>> 0);
                combined = (combined ^ ((opcode * _SM_MULT2) >>> 0)) >>> 0;
                combined = (combined ^ ((pc * _SM_MULT3) >>> 0)) >>> 0;
                return _smMixBits(combined);
            }

            // Generate permutation using Fisher-Yates shuffle
            function _smGeneratePermutation(seed, size) {
                let arr = [];
                for (let i = 0; i < size; i++) {
                    arr[i] = i;
                }
                let s = seed;
                for (let i = size - 1; i > 0; i--) {
                    s = _smMixBits(s ^ i);
                    let j = s % (i + 1);
                    let tmp = arr[i];
                    arr[i] = arr[j];
                    arr[j] = tmp;
                }
                return arr;
            }

            // Cache for derived state maps
            let _smStateMapsCache = {};

            // Get or create state maps from seed
            function _smGetStateMaps(seed) {
                if (_smStateMapsCache[seed]) {
                    return _smStateMapsCache[seed];
                }

                let stateMaps = [];
                for (let state = 0; state < _SM_NUM_STATES; state++) {
                    let stateSeed = _smMixBits(seed ^ (state * _SM_MULT1));
                    let permutation = _smGeneratePermutation(stateSeed, _SM_OPCODE_COUNT);

                    // Create reverse lookup: physicalOpcode -> logicalOpcode
                    let reverseMap = {};
                    for (let i = 0; i < _SM_OPCODE_COUNT; i++) {
                        reverseMap[permutation[i]] = i;
                    }
                    stateMaps.push(reverseMap);
                }

                _smStateMapsCache[seed] = stateMaps;
                return stateMaps;
            }

            // Encode a numeric value for stack (only for numbers)
            function _smEncodeValue(value, key) {
                if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value)) {
                    // Only encode 32-bit integers
                    if (value >= -2147483648 && value <= 2147483647) {
                        return (value ^ key) | 0;
                    }
                }
                return value;
            }

            // Decode a numeric value from stack
            function _smDecodeValue(value, key) {
                if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value)) {
                    if (value >= -2147483648 && value <= 2147483647) {
                        return (value ^ key) | 0;
                    }
                }
                return value;
            }
`;
}
function getStateMachineInitTemplate() {
    return `
                // State machine initialization (when vmStatefulOpcodes is enabled)
                // State is computed from seed and pc for each instruction (position-based, not history-based)
                let _smSeed = bc[{keySmSeed}] || 0;
                let _smStateMaps = _smSeed ? _smGetStateMaps(_smSeed) : null;
`;
}
function getStackEncodingInitTemplate() {
    return `
                // Stack encoding initialization (when vmStackEncoding is enabled)
                // Override stack methods to transparently encode/decode numeric values
                if (bc[{keySeKey}] !== undefined) {
                    let _seKey = bc[{keySeKey}];
                    let _origPush = stack.push.bind(stack);
                    let _origPop = stack.pop.bind(stack);
                    stack.push = function(v) {
                        // Only encode 32-bit integers
                        if (typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v) && v >= -2147483648 && v <= 2147483647) {
                            return _origPush((v ^ _seKey) | 0);
                        }
                        return _origPush(v);
                    };
                    stack.pop = function() {
                        let v = _origPop();
                        // Only decode 32-bit integers
                        if (typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v) && v >= -2147483648 && v <= 2147483647) {
                            return (v ^ _seKey) | 0;
                        }
                        return v;
                    };
                }
`;
}
function getStatefulOpcodeResolutionTemplate() {
    return `
                            // State-dependent opcode resolution
                            if (_smStateMaps) {
                                // Resolve physical opcode to logical opcode using current state
                                logicalOp = _smStateMaps[_smState][logicalOp];
                            }
`;
}
function getStateEvolutionTemplate() {
    return `
                            // Evolve state machine state
                            if (_smStateMaps) {
                                _smState = _smEvolveState(_smState, logicalOp, pc - 1, _smPrevOpcode);
                                _smPrevOpcode = logicalOp;
                            }
`;
}
function getStackEncodingPushTemplate() {
    return `
            // Encoded stack push
            function _sePush(stack, value, _seKey, state, opcode, pc) {
                let encodedValue = _smEncodeValue(value, _seKey);
                stack.push(encodedValue);
                return _smEvolveStackKey(_seKey, state, opcode, pc);
            }
`;
}
function getStackEncodingPopTemplate() {
    return `
            // Encoded stack pop
            function _sePop(stack, _seKey, state, opcode, pc) {
                let encodedValue = stack.pop();
                let decodedValue = _smDecodeValue(encodedValue, _seKey);
                return { value: decodedValue, newKey: _smEvolveStackKey(_seKey, state, opcode, pc) };
            }
`;
}
